/**
 * Screen 5 — Contest detail / Join.
 *
 * Wireframe shape:
 *   - dark hero with category + question count
 *   - GROUP / SKILL-BASED tag row
 *   - 3-cell stat card (Entry / Prize pool / Paid out)
 *   - "How it works" sentence
 *   - KYC note (gold/teal depending on status)
 *   - gold "Join for ₦X" CTA
 *
 * Hits /contest/:id/rules for an atomic snapshot of fee + pool + rake + KYC
 * gate. The join POST is itself atomic on the server (DR wallet, CR pool).
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';

export async function renderContest(root) {
  const parts = window.location.pathname.split('/');
  const contestId = parts[parts.length - 1];

  root.innerHTML = `
    <div class="appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Contest</div>
    </div>
    <div class="pad" id="contest-page">
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;

  let rules;
  try {
    rules = await api(`/contest/${encodeURIComponent(contestId)}/rules`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    toast.fromError(err);
    document.getElementById('contest-page').innerHTML =
      `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }
  paintRules(rules);

  document.getElementById('join-btn')?.addEventListener('click', () => onJoin(rules));
  document.getElementById('cancel-btn')?.addEventListener('click', () => onCancel(rules));
}

function rakePctLabel(bps) {
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 1)}%`;
}

function statusTag(status) {
  switch (status) {
    case 'open':     return '<span class="tag vio">OPEN</span>';
    case 'locked':   return '<span class="tag teal">IN PROGRESS</span>';
    case 'settled':  return '<span class="tag gold">SETTLED</span>';
    case 'refunded': return '<span class="tag mag">REFUNDED</span>';
    default: return `<span class="tag vio">${escape(status)}</span>`;
  }
}

function paintRules(r) {
  const me = auth.getMe();
  const kycOk = (me?.kyc_level ?? 0) >= r.kyc_required;
  const canJoin = !r.you_joined && r.status === 'open' && r.seats_filled < r.capacity;
  const canCancel = r.you_joined && r.status === 'open' && r.seats_filled === 1;

  document.getElementById('contest-page').innerHTML = `
    <div class="hero dark" style="height:96px;display:flex;align-items:flex-end;">
      <div>
        <div style="font-family:var(--font-display);font-size:18px;font-weight:600;">
          1-on-1 paid contest
        </div>
        <div style="font-size:12px;opacity:0.75;margin-top:2px;">
          ${r.capacity} players · winner takes all
        </div>
      </div>
    </div>

    <div class="row" style="margin-top:13px;">
      ${statusTag(r.status)}
      <span class="tag teal">SKILL-BASED</span>
      <div class="sp"></div>
      <span style="font-size:11.5px;color:var(--mute);font-weight:500;">
        ${r.seats_filled} / ${r.capacity} seats
      </span>
    </div>

    <div class="card">
      <div class="row">
        <div class="stat">
          <div class="v">${escape(formatKobo(r.entry_fee_kobo))}</div>
          <div class="k">Entry</div>
        </div>
        <div class="stat">
          <div class="v" style="color:var(--gold-t)">${escape(formatKobo(r.winner_payout_kobo))}</div>
          <div class="k">Winner gets</div>
        </div>
        <div class="stat">
          <div class="v">Top 1</div>
          <div class="k">Paid out</div>
        </div>
      </div>
    </div>

    <h3 class="sec">How it works</h3>
    <div style="font-size:13px;color:#B6AFD6;font-weight:500;line-height:1.9;">
      Score = speed + accuracy<br>
      Platform rake: ${rakePctLabel(r.rake_pct_bps)} · ties broken by total time<br>
      Instant payout to your wallet on win
    </div>

    ${
      r.kyc_required > 0
        ? `<div class="note ${kycOk ? 'teal' : 'gold'}">
            ${kycOk
              ? `Paid entry needs verified KYC — you are verified.`
              : `KYC level ${r.kyc_required}+ required to play paid contests. <a href="/kyc" data-link style="color:inherit;text-decoration:underline;">Verify now</a>.`
            }
          </div>`
        : ''
    }

    <div id="contest-status"></div>

    <div class="cta-row">
      ${canJoin
        ? `<button type="button" id="join-btn" class="btn gold" ${kycOk ? '' : 'disabled'}>
             Join for ${escape(formatKobo(r.entry_fee_kobo))}
           </button>`
        : ''}
      ${r.you_joined && r.status !== 'open'
        ? `<a href="/match/${escape(r.match_id)}" data-link class="btn gold">Open match</a>`
        : ''}
      ${canCancel
        ? `<button type="button" id="cancel-btn" class="btn ghost">Cancel &amp; refund stake</button>`
        : ''}
      ${r.status === 'settled' || r.status === 'refunded'
        ? `<a href="/match/${escape(r.match_id)}/result" data-link class="btn ghost">See result</a>`
        : ''}
    </div>
  `;
}

function setLocalStatus(message, kind = 'info') {
  const el = document.getElementById('contest-status');
  if (!el) return;
  if (!message) {
    el.innerHTML = '';
    return;
  }
  const color = kind === 'error' ? 'var(--pink-t)' :
                kind === 'success' ? 'var(--teal-t)' :
                'var(--ink-2)';
  el.innerHTML = `<div style="color:${color};margin-top:11px;font-weight:600;font-size:12.5px;">
    ${escape(message)}
  </div>`;
}

async function onJoin(rules) {
  const btn = document.getElementById('join-btn');
  if (btn) btn.disabled = true;
  setLocalStatus(`Staking ${formatKobo(rules.entry_fee_kobo)}…`);
  try {
    const res = await api(`/contest/${encodeURIComponent(rules.contest_id)}/join`, { method: 'POST' });
    toast.success(`Stake collected. Balance: ${formatKobo(res.your_balance_kobo)}.`);
    setTimeout(() => navigate(`/match/${res.match_id}`), 400);
  } catch (err) {
    setLocalStatus(err.message || 'Could not join this contest.', 'error');
    if (btn) btn.disabled = false;
  }
}

async function onCancel(rules) {
  const btn = document.getElementById('cancel-btn');
  if (btn) btn.disabled = true;
  setLocalStatus('Cancelling and refunding your stake…');
  try {
    await api(`/match/${encodeURIComponent(rules.match_id)}/invite/cancel`, { method: 'POST' });
    toast.success('Cancelled. Stake refunded to your wallet.');
    setTimeout(() => navigate('/wallet'), 500);
  } catch (err) {
    setLocalStatus(err.message || 'Could not cancel.', 'error');
    if (btn) btn.disabled = false;
  }
}
